import { matchPath } from "react-router-dom";
import { APPLICATION_CONNECTION } from "routes/paths";
import { idpTypes } from "@p81-common/shell-core-js";
import { env } from "@p81-common/shell-core-js";

let {
  REACT_APP_AUTH0_CDN_URL,
  REACT_APP_DOMAIN,
  REACT_APP_CHANGE_WORKSPACE_URL,
} = env;

export function maybeGetAppLoginData(search) {
  try {
    let from = new URLSearchParams(search).get("from");

    if (from) {
      const location = JSON.parse(from);

      if (
        matchPath(location.pathname, { path: APPLICATION_CONNECTION }) &&
        location.search
      ) {
        let app = new URLSearchParams(location.search).get("app");

        if (app) {
          return JSON.parse(atob(app));
        }
      }
    }
  } catch {}

  return null;
}

export function maybeImplicitToken(search) {
  try {
    let encodedToken = new URLSearchParams(atob(search.slice(1))).get(
      "implicit_token"
    );
    if (encodedToken) {
      return decodeURIComponent(encodedToken);
    }
  } catch (e) {}
}

export let enterpriseStrategies = [
  "ad",
  "adfs",
  "auth0-adldap",
  "auth0-oidc",
  "custom",
  "google-apps",
  "ip",
  "mscrm",
  "office365",
  "pingfederate",
  "samlp",
  "sharepoint",
  "waad",
  "oidc",
];

export const obtainError = "failed to obtain access token";

export const invalidStateError = "State does not match";

export const invalidRedirectUrlError = "Invalid redirect url";

export function isEmail(str) {
  if (typeof str !== "string") return;
  let trimmed = str.trim();
  return (
    trimmed.includes("@") && trimmed.includes(".") && !trimmed.includes(" ")
  );
}

export function getEmailDomain(str) {
  if (!isEmail(str)) {
    return "";
  }
  return str.split("@")[1].toLowerCase();
}

export function isSSOEnabled(values, clientConfig) {
  let emailDomain = getEmailDomain(values.username);

  if (clientConfig) {
    return Object.keys(clientConfig.ssoDomains).find((connectionName) =>
      clientConfig.ssoDomains[connectionName].includes(emailDomain)
    );
  }
}

export function getClientConfig(clientId) {
  return new Promise((resolve) => {
    window.Auth0 = {};
    window.Auth0.setClient = function (json) {
      json.ssoDomains = {};
      for (let strategy of json.strategies) {
        if (enterpriseStrategies.includes(strategy.name)) {
          for (let connection of strategy.connections) {
            let domains = connection.domain_aliases || [];
            if (connection.domain) {
              domains.unshift(connection.domain);
            }
            json.ssoDomains[connection.name] = domains;
          }
        }
      }
      resolve(json);
    };
    let script = document.createElement("script");
    script.src = `${REACT_APP_AUTH0_CDN_URL}/client/${clientId}.js?t${+new Date()}`;
    document.body.appendChild(script);
  });
}

export function normalizeError(error) {
  if (!error) {
    return error;
  }

  if (error.code === "unauthorized") {
    if (!error.description || error.description === "access_denied") {
      return {
        ...error,
        code: "lock_unauthorized",
        error: "lock_unauthorized",
        description: error.description || "Permissions were not granted.",
      };
    }

    if (error.description === "user is blocked") {
      return {
        ...error,
        code: "blocked_user",
        error: "blocked_user",
        description: error.description,
      };
    }

    return {
      ...error,
      code: "rule_error",
      error: "rule_error",
      description: error.description,
    };
  }

  if (error.error === "access_denied" || error.code === "access_denied") {
    return {
      ...error,
      code: "invalid_user_password",
      error: "invalid_user_password",
      description: error.description,
    };
  }

  let result = {
    ...error,
    error: error.code ? error.code : error.statusCode || error.error,
    description: error.description || error.code,
  };

  return result.error === undefined && result.description === undefined
    ? error
    : result;
}

export function loginErrorMessage(error) {
  if (error.status === 0) {
    return "lock_network";
  }

  if (error.code === "rule_error") {
    return error.description || "lock_fallback";
  }

  let code = error.error || error.code;
  if (code === "invalid_user_password") {
    code = "lock_invalid_username_password";
  }

  if (code === "a0.mfa_registration_required") {
    code = "lock_mfa_registration_required";
  }

  if (code === "a0.mfa_invalid_code") {
    code = "lock_mfa_invalid_code";
  }

  if (code === "password_expired") {
    code = "password_change_required";
  }

  let result = code || "lock_fallback";

  return `auth0.${result}`;
}

export let validCallbacks = [
  "com.perimeter81.linux://callback",
  "com.perimeter81.android://callback",
  `safervpn://${REACT_APP_DOMAIN}/callback`,
  `perimeter81://${REACT_APP_DOMAIN}/callback`,
  `perimeter81://${REACT_APP_DOMAIN}/macos/callback`,
  `HarmonySASE://${REACT_APP_DOMAIN}/callback`,
  `HarmonySASE://${REACT_APP_DOMAIN}/macos/callback`,
  `CheckPointSASE://${REACT_APP_DOMAIN}/callback`,
  `CheckPointSASE://${REACT_APP_DOMAIN}/macos/callback`,
  `com.safervpn.business.ios://auth.${REACT_APP_DOMAIN}/ios/com.safervpn.business.ios/callback`,
  `com.perimeter81.business.ios://auth.${REACT_APP_DOMAIN}/ios/com.perimeter81.business.ios/callback`,
];

export function getEnabledIdps(idps) {
  let result = [];
  for (let idpType in idps) {
    if (idpType !== idpTypes.database && idps[idpType].length > 0) {
      let connections = idps[idpType].filter((idp) => idp.enabled);
      if (connections.length) {
        result.push({
          idpType,
          connections,
        });
      }
    }
  }
  return result;
}

export function idpsToArray(idps, username) {
  let result = [];
  for (let i in idps) {
    if (i !== idpTypes.database && idps[i].length > 0) {
      let domainFromEmail = getEmailDomain(username);
      let connection = domainFromEmail
        ? idps[i].find(
            (conn) =>
              conn.domainAliases.includes(domainFromEmail) ||
              conn.domain === domainFromEmail
          )
        : false;

      let enabledIdps = idps[i].filter((idp) => idp.enabled);
      if (connection?.enabled || enabledIdps.length) {
        result.push({
          idpType: i,
          connections: connection ? [connection] : enabledIdps,
        });
      }
    }
  }
  return result;
}

export function getUrlWithParams() {
  let url = new URL(REACT_APP_CHANGE_WORKSPACE_URL);
  let params = new URLSearchParams(window.location.search);
  params.set("allowTenantSelect", "");
  url.search = params;
  return url;
}
